import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

/** Id del producto en vanti.infra.proyectos (habilita Variables de entorno y Arquitectura). */
export const ANOTACION_INFRA = 'grupovanti.com/infra';

export type ConfigShared = {
  id: string;
  titulo: string;
  service: string;
  cuenta?: string;
  repoProducto: string;
  ambientes: string[];
  simulacion: boolean;
  /** Endpoint del emulador de AWS (LocalStack) si se usa. */
  emulador?: string;
  rama: string;
  aprobaciones: {
    ambientes: string[];
    requeridas: number;
    vigenciaHoras: number;
    modoPrueba: boolean;
  };
  usuario: string;
  /** Grupos del usuario (vanti-usuarios) y lo que le permiten. */
  grupos: string[];
  puedeEditar: boolean;
  puedeAprobar: boolean;
};

export type ServicioResumen = {
  clave: string;
  nombre: string;
  puerto?: number;
  cpu?: number;
  memoria?: number;
  rutas: string[];
  variables: number;
};

export type MetaParametro = {
  ruta: string;
  tipo: string;
  version: number;
  modificado?: string;
  modificadoPor?: string;
};

export type Variable =
  | {
      tipo: 'plana';
      nombre: string;
      valor: string;
      /** Parece un secreto en claro en el tfvars: el backend no manda el valor. */
      sensible?: boolean;
    }
  | {
      tipo: 'ssm';
      nombre: string;
      location: string;
      ruta: string;
      existe: boolean;
      meta?: MetaParametro;
    }
  | { tipo: 'secreto'; nombre: string; secreto: string; jsonKey: string };

export type Solicitud = {
  id: string;
  /** actualizar/crear: parámetro SSM; *-fija: valor del tfvars. */
  tipo:
    | 'actualizar'
    | 'crear'
    | 'actualizar-fija'
    | 'crear-fija'
    | 'crear-microservicio'
    | 'crear-persistencia'
    | 'mover-a-ssm'
    | 'actualizar-secreto';
  ambiente: string;
  servicio: string;
  variable: string;
  location: string;
  motivo: string;
  solicitante: string;
  estado: 'pendiente' | 'aplicando' | 'aplicada' | 'rechazada' | 'vencida' | 'fallida';
  creada: string;
  vence: string;
  resultado?: string;
  rechazadaPor?: string;
  /** Solo variables fijas: valor propuesto y el que reemplaza. */
  valorVisible?: string;
  valorAnterior?: string;
  aprobaciones: Array<{ usuario: string; fecha: string }>;
};

export type Evento = {
  fecha: string;
  proyecto: string;
  usuario: string;
  accion: string;
  detalle: string;
};

export type VariablesServicio = {
  servicio: { clave: string; nombre: string; rutas: string[] };
  requiereAprobacion: boolean;
  variables: Variable[];
  pendientes: Solicitud[];
};

export type RespuestaCambio = {
  estado: 'aplicada' | 'pendiente';
  resultado?: string;
  solicitud?: Solicitud;
};

/** Cliente del plugin de backend vanti-infra para un producto. */
export class InfraApi {
  constructor(
    private readonly discovery: DiscoveryApi,
    private readonly fetch: FetchApi,
    /** Id del producto (anotación grupovanti.com/infra). */
    private readonly proyecto: string,
    /** Solo con aprobaciones.modoPrueba: identidad simulada para probar. */
    private readonly usuarioPrueba?: string,
  ) {}

  private async pedir<T>(ruta: string, init?: RequestInit): Promise<T> {
    const base = await this.discovery.getBaseUrl('vanti-infra');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.usuarioPrueba) headers['x-vanti-usuario-prueba'] = this.usuarioPrueba;
    const res = await this.fetch.fetch(`${base}${ruta}`, { ...init, headers });
    const cuerpo = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(cuerpo.error ?? `Error ${res.status}`);
    return cuerpo as T;
  }

  config() {
    return this.pedir<ConfigShared>(`${this.p}/config`);
  }

  servicios(ambiente: string) {
    return this.pedir<ServicioResumen[]>(`${this.p}/ambientes/${ambiente}/servicios`);
  }

  variables(ambiente: string, servicio: string) {
    return this.pedir<VariablesServicio>(
      `${this.p}/ambientes/${ambiente}/servicios/${encodeURIComponent(servicio)}/variables`,
    );
  }

  actualizar(ambiente: string, servicio: string, nombre: string, valor: string, motivo: string) {
    return this.pedir<RespuestaCambio>(
      `${this.p}/ambientes/${ambiente}/servicios/${encodeURIComponent(servicio)}/variables/${encodeURIComponent(nombre)}`,
      { method: 'PUT', body: JSON.stringify({ valor, motivo }) },
    );
  }

  crear(
    ambiente: string,
    servicio: string,
    datos: {
      tipo: 'ssm' | 'fija';
      nombre: string;
      location?: string;
      valor?: string;
      motivo: string;
    },
  ) {
    return this.pedir<RespuestaCambio>(
      `${this.p}/ambientes/${ambiente}/servicios/${encodeURIComponent(servicio)}/variables`,
      { method: 'POST', body: JSON.stringify(datos) },
    );
  }

  /** Secreto en claro del tfvars → SSM con un valor nuevo (MR). */
  moverASsm(
    ambiente: string,
    servicio: string,
    nombre: string,
    datos: { valor: string; location?: string; motivo: string },
  ) {
    return this.pedir<RespuestaCambio>(
      `${this.p}/ambientes/${ambiente}/servicios/${encodeURIComponent(servicio)}/variables/${encodeURIComponent(nombre)}/mover-a-ssm`,
      { method: 'POST', body: JSON.stringify(datos) },
    );
  }

  /** Bitácora de cambios del producto (más recientes primero). */
  auditoria(limite = 30) {
    return this.pedir<Evento[]>(`${this.p}/auditoria?limite=${limite}`);
  }

  /** Solicitudes del producto. */
  solicitudes(estado?: string) {
    const qs = new URLSearchParams({ proyecto: this.proyecto });
    if (estado) qs.set('estado', estado);
    return this.pedir<Solicitud[]>(`/solicitudes?${qs}`);
  }

  private get p() {
    return `/proyectos/${encodeURIComponent(this.proyecto)}`;
  }

  aprobar(id: string) {
    return this.pedir<Solicitud>(`/solicitudes/${id}/aprobar`, { method: 'POST' });
  }

  rechazar(id: string) {
    return this.pedir<Solicitud>(`/solicitudes/${id}/rechazar`, { method: 'POST' });
  }
}

export const nombreUsuario = (ref: string) => ref.replace(/^user:(default\/)?/, '');

export const fechaCorta = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';
